// SourceTypes.ts — JSON-only sources (no legacy markdown parsing)

import i18n from '@/i18n';

/** One source item attached to a chat message (rendered in Sources panel). */
export interface SourceItem {
  /** Stable id for linking from [n] markers; UI may assign source-${index} if absent. */
  id?: string;
  /** Human-friendly title (falls back to hostname when missing). */
  title?: string;
  /** Canonical, clickable URL (http/https). */
  url?: string;
  /** Optional type label for UI chips/icons. */
  type?:
    | 'website'
    | 'democracy_works'
    | 'government'
    | 'news'
    | 'candidate'
    | 'nonprofit'
    | 'ballotpedia';
  /** Whether the link is clickable; if omitted, inferred from url presence. */
  isAvailable?: boolean;
  /** Short blurb shown under the title (keep it brief). */
  description?: string;
  /**
   * Set to `'publisher'` when `description` is the page's own words — a Brave
   * snippet, or the snippet a profile captured for its evidence URL. Absent when
   * VoteMate wrote it (the candidate-profile worker's stance summaries land in
   * the same field), and the Sources menu then shows no blurb: a line under a
   * publisher's name and favicon reads as that publisher's words whatever label
   * sits in front of it.
   */
  description_source?: 'publisher' | 'quote' | 'extract' | 'dataset';
  /**
   * The date the record stamps on a `quote`. A quote a voter cannot place in
   * time is worth much less on a race that has moved since.
   */
  description_date?: string;
  /**
   * Who or what this source is about — the candidate whose profile it evidences,
   * the subject of an endorsement page — when the producing tool knew. Present
   * as a field rather than only inside `title`, because a title tells a reader
   * whose source this is and a field lets the renderer refuse a citation that
   * puts it on someone else's claim. See `makeSubjectGuard`. General web results
   * carry none and are never constrained.
   */
  subject?: string;
  /**
   * Which position of `subject` this source evidences — the profile's topic
   * label. Paired with `subject` it is the whole of what the profile asserts,
   * which is what lets `repairCitations` move a misplaced citation onto the
   * right source rather than only refuse it.
   */
  topic?: string;
  /**
   * Set to `'subject'` when the source covers `subject` as a whole — their
   * Ballotpedia profile, their campaign site — rather than the one position it
   * was cited for. `repairCitations` cites it for a claim no topic-specific
   * source answers to, which is most of them: a profile holds four or five
   * topics and an answer about that candidate writes a dozen claims. Carried
   * rather than inferred, and default-deny like `description_source` — an
   * unmarked source stands for one claim only, so a producer that forgets the
   * flag under-cites rather than putting an unrelated article behind a sentence.
   */
  scope?: 'subject';
  /**
   * Global citation index assigned by the backend's source-index wrapper.
   * The LLM emits ``[N]`` markers using this value, and the frontend uses
   * it as the lookup key so citations resolve correctly even when raw
   * sources arrive out of order from parallel tool calls.
   */
  cite_index?: number;
}

/**
 * Canonical key used to dedupe source URLs: hostname + path + query, no hash.
 * Two URLs that point at the same page but differ only by tracking params or a
 * trailing slash map to the same key — that's why both this helper and the
 * citation-lookup in BotMessage rely on it.
 *
 * The **query is part of the page's identity**, and leaving it out is what tied
 * one candidate's source to another candidate's fact. A profile's evidence URL
 * is routinely `youtube.com/watch?v=…`, and a run of Ballotpedia links can share
 * one `index.php` path; keyed on host+path alone, every such page collapsed into
 * a single row, and each `[N]` pointing at any of them resolved to whichever
 * landed first. The chip then carried that page's title — "Griselda Romero —
 * Healthcare" on a claim about someone else.
 *
 * `normalizeUrl` has already dropped the tracking params by the time a URL
 * reaches here; sorting what remains keeps `?a=1&b=2` and `?b=2&a=1` one key.
 * Plain string work rather than `URLSearchParams` iteration, so web and React
 * Native compute the identical key on the identical URL.
 */
export function urlDedupKey(url: string): string {
  try {
    const u = new URL(url);
    const query = u.search.replace(/^\?/, '').split('&').filter(Boolean).sort().join('&');
    const path = u.pathname.replace(/\/+$/, '');
    return `${u.hostname.toLowerCase()}${path}${query ? `?${query}` : ''}`;
  } catch {
    return url;
  }
}

/**
 * Identity of a *raw* backend source, for merging two arrays that describe the
 * same turn (the cumulative array the end frame ships and this turn's own).
 *
 * `cite_index` when the backend stamped one — it is the number the body cites,
 * so it identifies the source more precisely than its URL does — and the deduped
 * URL otherwise, which is what an unstamped entry (an officeholder profile
 * attached after the stream) has to be recognised by.
 */
export function rawSourceIdentity(source: { cite_index?: unknown; url?: unknown }): string {
  if (typeof source?.cite_index === 'number') return `ci:${source.cite_index}`;
  const url = typeof source?.url === 'string' ? source.url.trim() : '';
  return url ? `url:${urlDedupKey(normalizeUrl(url))}` : `obj:${JSON.stringify(source)}`;
}

/** Sanitize and dedupe a backend-provided sources array. */
export function sanitizeSources(raw: any[] = []): SourceItem[] {
  const out: SourceItem[] = [];
  const seen = new Set<string>();

  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;

    const url = typeof (s as any).url === 'string' ? (s as any).url.trim() : '';
    if (!url || !/^https?:\/\//i.test(url)) continue;

    const normalized = normalizeUrl(url);
    const key = urlDedupKey(normalized);
    if (seen.has(key)) continue;
    seen.add(key);

    const title =
      typeof (s as any).title === 'string' && (s as any).title.trim()
        ? (s as any).title.trim()
        : extractTitleFromUrl(normalized);

    const description =
      typeof (s as any).description === 'string' ? (s as any).description.trim() : undefined;
    // Default-deny against the known set, matching the backend: an invented or
    // misspelled marker gets the blurb hidden rather than rendered as something
    // it is not. `sourceSubtext` turns the surviving value into a form.
    const rawSource = (s as any).description_source;
    const descriptionSource = DESCRIPTION_SOURCES.has(rawSource)
      ? (rawSource as NonNullable<SourceItem['description_source']>)
      : undefined;
    const descriptionDate =
      descriptionSource && typeof (s as any).description_date === 'string'
        ? (s as any).description_date.trim() || undefined
        : undefined;

    // Prefer a type the backend already knows. It has ground truth the
    // frontend cannot derive — a candidate's own campaign site comes from the
    // ballot's candidate record, Ballotpedia enrichment is tagged at the
    // source. The heuristic below is for domains nobody has classified.
    const backendType = (s as any).type;
    const type = KNOWN_SOURCE_TYPES.has(backendType)
      ? (backendType as SourceItem['type'])
      : determineSourceType(normalized, title || '');

    const citeIndex = (s as any).cite_index;
    const subject = typeof (s as any).subject === 'string' ? (s as any).subject.trim() : '';
    const topic = typeof (s as any).topic === 'string' ? (s as any).topic.trim() : '';
    // Default-deny, matching the backend: only the explicit marker survives, so
    // a source nobody vouched for as covering its whole subject is never cited
    // for a claim it did not evidence.
    const scope = (s as any).scope === 'subject' ? ('subject' as const) : undefined;
    out.push({
      id: (s as any).id, // UI may assign if not present
      title,
      url: normalized,
      description,
      description_source: descriptionSource,
      description_date: descriptionDate,
      subject: subject || undefined,
      topic: topic || undefined,
      scope,
      type,
      isAvailable: (s as any).isAvailable ?? true,
      cite_index: typeof citeIndex === 'number' ? citeIndex : undefined,
    });
  }
  return out;
}

/** Drop common tracking params; lowercases host; preserves scheme/path. */
export function normalizeUrl(u: string): string {
  try {
    const obj = new URL(u);
    obj.protocol = obj.protocol || 'https:';
    obj.hostname = obj.hostname.toLowerCase();

    // strip tracking query params
    const disallow = /^(utm_|fbclid|gclid|mc_eid)/i;
    const params = new URLSearchParams(obj.search);
    for (const key of Array.from(params.keys())) {
      if (disallow.test(key)) params.delete(key);
    }
    obj.search = params.toString() ? `?${params.toString()}` : '';
    return obj.toString();
  } catch {
    return u;
  }
}

/** Derive a readable title from a URL hostname (with small hints). */
export function extractTitleFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    let title = urlObj.hostname;
    if (title.startsWith('www.')) title = title.substring(4);
    title = title.charAt(0).toUpperCase() + title.slice(1);
    // TLD, not a substring: `hostname.includes('gov')` also matched
    // `governance.propublica.org` and `mygov.vote.org`, appending "Official
    // Website" to a nonprofit. Same class of bug as the classification rewrite,
    // in the display fallback rather than the chip.
    const tld = urlObj.hostname.split('.').filter(Boolean).pop();
    if (tld === 'gov' || tld === 'mil')
      title += i18n.t('sources.officialWebsiteSuffix', { ns: 'chat' });
    else if (tld === 'edu') title += i18n.t('sources.educationalResourceSuffix', { ns: 'chat' });
    return title;
  } catch {
    return i18n.t('sources.externalSource', { ns: 'chat' });
  }
}

/**
 * Provenance markers a client may render. Mirrors the backend's
 * `DESCRIPTION_SOURCES`; a value outside it takes the default-deny path, so a
 * producer that invents one under-prints rather than rendering text as a kind it
 * is not. `sourceSubtext` maps each to a form.
 */
const DESCRIPTION_SOURCES = new Set(['publisher', 'quote', 'extract', 'dataset']);

/** Types the backend may assert directly; anything else is re-derived here. */
const KNOWN_SOURCE_TYPES = new Set([
  'website',
  'democracy_works',
  'government',
  'news',
  'candidate',
  'nonprofit',
  'ballotpedia',
]);

/**
 * Registrable domain → source type. One entry per publisher, which is the whole
 * maintenance cost of adding one.
 *
 * Matching is on the **hostname**, never the full URL or the page title. That
 * distinction is the fix for a real misclassification: the previous version
 * tested `title.includes('official')`, so an AP headline reading "Tuberville
 * officially enters the race" was labelled Official Government. Substring tests
 * against prose will always find words inside other words; hostnames are a
 * closed, checkable vocabulary.
 */
const SOURCE_TYPE_BY_DOMAIN: Record<string, NonNullable<SourceItem['type']>> = {
  'ballotpedia.org': 'ballotpedia',
  // News. Domains whose names do not contain "news" are exactly the ones a
  // keyword heuristic misses, which is why they are enumerated.
  'apnews.com': 'news',
  'ap.org': 'news',
  'reuters.com': 'news',
  'npr.org': 'news',
  'pbs.org': 'news',
  'bbc.com': 'news',
  'bbc.co.uk': 'news',
  'cnn.com': 'news',
  'politico.com': 'news',
  'thehill.com': 'news',
  'axios.com': 'news',
  'propublica.org': 'news',
  'washingtonpost.com': 'news',
  'nytimes.com': 'news',
  'wsj.com': 'news',
  'usatoday.com': 'news',
  'aljazeera.com': 'news',
  'foxnews.com': 'news',
  'nbcnews.com': 'news',
  'cbsnews.com': 'news',
  'abcnews.go.com': 'news',
  'ballotwire.com': 'news',
  'alreporter.com': 'news',
  'tuscaloosathread.com': 'news',
  'al.com': 'news',
  // Public-radio member stations. Call-sign-shaped `.org` newsrooms that would
  // otherwise read as Nonprofit — true of the licensee, wrong for the citation,
  // and these are exactly the outlets that cover local races.
  'wbur.org': 'news',
  'wnyc.org': 'news',
  'kqed.org': 'news',
  'wamu.org': 'news',
  'wfae.org': 'news',
  'wbez.org': 'news',
  'kut.org': 'news',
  'wesa.fm': 'news',
  // Nonpartisan civic orgs that would otherwise fall through to `nonprofit`.
  'vote.org': 'nonprofit',
  'league.org': 'nonprofit',
  'lwv.org': 'nonprofit',
};

/** Hoisted so the map is not re-materialized for every source on every message.
 *  The map is designed to grow, which is what makes the per-call allocation and
 *  linear scan worth avoiding.
 *
 *  Sorted longest-domain-first so a subdomain entry answers before its parent.
 *  `hostMatchesDomain` accepts subdomains, so a `go.com` entry reached before
 *  `abcnews.go.com` would claim it — sorting removes that as a thing anyone has
 *  to remember when adding a line to the map above. Matches the backend's
 *  `_DOMAINS_BY_SPECIFICITY`. */
const SOURCE_TYPE_ENTRIES = (
  Object.entries(SOURCE_TYPE_BY_DOMAIN) as Array<[string, NonNullable<SourceItem['type']>]>
).sort((a, b) => b[0].length - a[0].length);

/** The Democracy Works sentinel, matched as a whole token. `\b` rather than a
 *  lookbehind so this stays identical in the mobile client, whose engine has not
 *  always supported one. Mirrors `_DEMOCRACY_WORKS_TITLE` in `source_extractor.py`. */
const DEMOCRACY_WORKS_TITLE = /\bdemocracy_works\b/i;

/** State election authorities that predate `.gov` adoption still serve from `.us`
 *  (`sos.state.mn.us`). What makes that checkable is the *delegation*, not the
 *  words in the host: under RFC 1480 the `state.<st>.us` branch belongs to that
 *  state's government and is not open registration.
 *
 *  Reading an election-authority label anywhere in the host would be a guess —
 *  `.us` has been an open registry since 2002, so anyone can register `evil.us`
 *  and serve `sos.evil.us`, which is the shape a phishing link takes. The state
 *  code is validated against `US_STATE_NAMES` for the same reason. A legacy host
 *  outside this structure reads as `website` until it is named in the map above. */
function isStateGovernmentUsHost(parts: string[]): boolean {
  return (
    parts.length >= 3 &&
    parts[parts.length - 1] === 'us' &&
    Object.prototype.hasOwnProperty.call(US_STATE_NAMES, parts[parts.length - 2]) &&
    parts[parts.length - 3] === 'state'
  );
}

/** Fallback classification for the UI chip/icon — hostname-driven.
 *
 *  Applies only to sources the backend did not already type (see
 *  `sanitizeSources`). `maf/source_types.py::classify_source` is the canonical
 *  implementation and runs the same rules server-side for everything the agent
 *  cites; keep the three in step when editing any of them.
 *
 *  Resolution order: explicit domain map → government/military TLD → a host under
 *  a state's `state.<st>.us` delegation → a domain label that begins or ends with
 *  "news" → candidate campaign site → `.org` → generic website. Every rule reads
 *  the hostname, so nothing in a URL path or a headline can change a category.
 */
export function determineSourceType(url: string, title: string): SourceItem['type'] {
  // `democracy_works` is a backend sentinel written into the title by an upstream
  // tool rather than prose, so a title check reaches it. Matched as a whole token
  // so a `democracy_workshop` writeup stays whatever its hostname says it is.
  if (DEMOCRACY_WORKS_TITLE.test(title || '')) {
    return 'democracy_works';
  }

  const host = hostnameOf(url);
  if (!host) return 'website';

  for (const [domain, type] of SOURCE_TYPE_ENTRIES) {
    if (hostMatchesDomain(host, domain)) return type;
  }

  const parts = host.split('.').filter(Boolean);
  const tld = parts[parts.length - 1];
  const label = parts.length >= 2 ? parts[parts.length - 2] : parts[0];

  // Government by TLD only. A `.gov` host is government by definition; a page
  // *about* government on a news site is not.
  if (tld === 'gov' || tld === 'mil') return 'government';
  if (isStateGovernmentUsHost(parts)) return 'government';

  // Domains that say what they are. Narrower than the old `url.includes('news')`,
  // which matched a path segment on any site, but it keeps the long tail of
  // local outlets (`abc7news.com`, `spectrumlocalnews.com`) out of `website`.
  if (label.startsWith('news') || label.endsWith('news')) return 'news';

  // Campaign sites. Kept as a list because a candidate's own domain is
  // unguessable — but this is per-cycle data the frontend should not be
  // carrying. The backend knows each candidate's website from the ballot
  // record; when it starts asserting `type: 'candidate'`, the branch above
  // honors it and this list can go.
  // Matched by domain, not exact host: campaign pages a research agent is
  // likely to cite often live on a subdomain (`donate.`, `secure.`, `act.`).
  for (const domain of CANDIDATE_DOMAINS) {
    if (hostMatchesDomain(host, domain)) return 'candidate';
  }

  if (tld === 'org') return 'nonprofit';

  return 'website';
}

/** Campaign domains, matched on hostname (not anywhere in the URL). */
const CANDIDATE_DOMAINS = new Set([
  'mikiesherrill.com',
  'jack4nj.com',
  'abigailspanberger.com',
  'winsomeforgovernor.com',
  'vic4liberty.com',
  'johnreidforvirginia.com',
  'jasonmiyares.com',
  'jayjones.com',
  'donnaforva.com',
  'jonesforva.com',
  'ghazalaforvirginia.com',
  'votefrankieflip.com',
  'hopeforvirginia.org',
  'victoriaproffitt.com',
  'jennifernap.com',
  'davidmick.com',
  'friendsofdaverosado.com',
  'wallacechadwick.com',
  'aprilforalgonkian.com',
  'maloneforalgonkian.net',
  'svensonforschoolboard.com',
  'sam4broadrun.com',
  'jonpepperforschoolboard.com',
  'munozforva.com',
  'laurenforleesburg.com',
  'arben4sterling.com',
  'amy4sterling.com',
  'blythescottfornorfolk.com',
  'fatehinorfolk.com',
  'joebaronforsheriff.com',
  'colette4rva.com',
  'irvingforsheriff.com',
  'votephil.net',
  'colinstolle.com',
  'rockyholcombvbsheriff.com',
  'voteleighhendersonvb.com',
]);

/**
 * True when *host* is *domain* or a subdomain of it.
 *
 * `hostnameOf` already strips a leading `www.`, so the exact-match arm covers
 * the common case; this exists for the rest — a campaign's `donate.`, `secure.`,
 * or `act.` host, or a publisher serving articles off a subdomain.
 */
function hostMatchesDomain(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/** Bare hostname (no leading www.) for a URL, or '' when unparseable. */
export function hostnameOf(url?: string): string {
  if (!url) return '';
  try {
    let host = new URL(url).hostname.toLowerCase();
    if (host.startsWith('www.')) host = host.slice(4);
    return host;
  } catch {
    return '';
  }
}

/**
 * Known publisher display names keyed by registrable domain. Covers the
 * proper-noun and acronym cases where a title-cased hostname would look wrong
 * (e.g. "Npr" → "NPR"). Anything not listed falls back to a title-cased
 * domain label, so this map only needs the outliers.
 */
const PUBLISHER_NAMES: Record<string, string> = {
  'ballotpedia.org': 'Ballotpedia',
  'npr.org': 'NPR',
  'pbs.org': 'PBS',
  'cnn.com': 'CNN',
  'bbc.com': 'BBC',
  'bbc.co.uk': 'BBC',
  'reuters.com': 'Reuters',
  'apnews.com': 'AP News',
  'ap.org': 'Associated Press',
  'foxnews.com': 'Fox News',
  'nbcnews.com': 'NBC News',
  'abcnews.go.com': 'ABC News',
  'cbsnews.com': 'CBS News',
  'politico.com': 'Politico',
  'washingtonpost.com': 'The Washington Post',
  'nytimes.com': 'The New York Times',
  'wsj.com': 'The Wall Street Journal',
  'usatoday.com': 'USA Today',
  'aljazeera.com': 'Al Jazeera',
  'news.google.com': 'Google News',
  'vote.org': 'Vote.org',
  'usa.gov': 'USA.gov',
  'fec.gov': 'FEC',
  'congress.gov': 'Congress.gov',
  'house.gov': 'House',
  'senate.gov': 'Senate',
};

/** Title-case a bare domain label (e.g. "ballotpedia" → "Ballotpedia"). */
function titleCaseLabel(label: string): string {
  return label
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** US state/territory codes → full names, for expanding `.gov`/`.us` domain
 *  labels like "ny" in governor.ny.gov to "New York" instead of "Ny". */
const US_STATE_NAMES: Record<string, string> = {
  al: 'Alabama',
  ak: 'Alaska',
  az: 'Arizona',
  ar: 'Arkansas',
  ca: 'California',
  co: 'Colorado',
  ct: 'Connecticut',
  de: 'Delaware',
  fl: 'Florida',
  ga: 'Georgia',
  hi: 'Hawaii',
  id: 'Idaho',
  il: 'Illinois',
  in: 'Indiana',
  ia: 'Iowa',
  ks: 'Kansas',
  ky: 'Kentucky',
  la: 'Louisiana',
  me: 'Maine',
  md: 'Maryland',
  ma: 'Massachusetts',
  mi: 'Michigan',
  mn: 'Minnesota',
  ms: 'Mississippi',
  mo: 'Missouri',
  mt: 'Montana',
  ne: 'Nebraska',
  nv: 'Nevada',
  nh: 'New Hampshire',
  nj: 'New Jersey',
  nm: 'New Mexico',
  ny: 'New York',
  nc: 'North Carolina',
  nd: 'North Dakota',
  oh: 'Ohio',
  ok: 'Oklahoma',
  or: 'Oregon',
  pa: 'Pennsylvania',
  ri: 'Rhode Island',
  sc: 'South Carolina',
  sd: 'South Dakota',
  tn: 'Tennessee',
  tx: 'Texas',
  ut: 'Utah',
  vt: 'Vermont',
  va: 'Virginia',
  wa: 'Washington',
  wv: 'West Virginia',
  wi: 'Wisconsin',
  wy: 'Wyoming',
  dc: 'Washington, D.C.',
};

/**
 * True when a title's trailing segment names the site it came from — its
 * letters and digits correspond to the host's registrable label.
 *
 * A trailing segment is only a publisher when the site agrees. "City & State NY"
 * on `cityandstateny.com` matches, and reads far better than the smushed domain
 * label — that is the case `publisherFromTitle` exists for. "Homelessness &
 * Housing" on `citywatchla.com` does not match, and is not a publisher at all:
 * the backend builds candidate-profile source titles as
 * `"{candidate} — {priority label}"`, where the priority label is one of
 * VoteMate's own free-form topic names. Trusting the segment unconditionally
 * printed "Homelessness & Housing" as the publisher on an immigration claim and
 * on three other unrelated bullets, which reads as a mismatched citation even
 * when the source resolved correctly.
 *
 * Checking against the hostname is what makes this decidable: the host is a
 * closed, verifiable fact about the source, while the title is prose we do not
 * control.
 */
function titleSegmentNamesHost(segment: string, host: string): boolean {
  // "&" spells out, because a masthead writes the ampersand its domain cannot:
  // "City & State NY" is `cityandstateny.com`, and dropping the character
  // instead would fail the very case this helper exists to keep. `&amp;` is
  // folded in the same step — scraped titles reach us both ways, and handling
  // only the bare character normalizes the entity to "andamp", which misses.
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/&(?:amp;)?/g, 'and')
      .replace(/[^a-z0-9]/g, '');
  const seg = normalize(segment);
  const parts = host.split('.').filter(Boolean);
  const label = normalize(parts.length >= 2 ? parts[parts.length - 2] : parts[0]);
  if (!label || seg.length < 3) return false;
  // Containment either way absorbs a leading article ("The Tuscaloosa Thread" →
  // `tuscaloosathread.com`) and a domain that appends its market or medium
  // ("Al Reporter" → `alreporter.com`).
  return label === seg || label.includes(seg) || seg.includes(label);
}

/**
 * Pull a publisher name from a page title's trailing segment, e.g.
 * "2026 State of the State | Governor Kathy Hochul | New York State" →
 * "New York State", or "Kathy Hochul - Ballotpedia" → "Ballotpedia". Only
 * fires when the title has a real delimiter (spaced pipe/dash) and the last
 * segment is short enough to read as a source name rather than a headline.
 */
function publisherFromTitle(title?: string): string | undefined {
  if (!title) return undefined;
  const segments = title
    .split(/\s+[|–—-]\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length < 2) return undefined;
  const last = segments[segments.length - 1];
  if (last.length < 2 || last.length > 32) return undefined;
  if (!/[A-Za-z]/.test(last)) return undefined;
  if (/^(home|homepage|official site|welcome)$/i.test(last)) return undefined;
  return last;
}

/**
 * Human-friendly publisher name for a source, used for the inline citation
 * chip (e.g. "Ballotpedia", "NPR"). Resolves in priority order: known
 * publisher map (incl. subdomains) → title-cased domain label → source-type
 * label → generic "Web". Grounds the label in the source's own domain so the
 * chip names the actual publisher rather than a category.
 */
export function getPublisherName(source: SourceItem): string {
  const host = hostnameOf(source.url);
  if (host) {
    if (PUBLISHER_NAMES[host]) return PUBLISHER_NAMES[host];
    for (const domain of Object.keys(PUBLISHER_NAMES)) {
      if (host.endsWith(`.${domain}`)) return PUBLISHER_NAMES[domain];
    }
    const parts = host.split('.').filter(Boolean);
    const tld = parts[parts.length - 1];
    // The registrable label sits just before the TLD (e.g. "virginia" in
    // "elections.virginia.gov").
    const label = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
    // Government domains: expand a 2-letter state/territory code (governor.ny.gov
    // → "New York") so it reads as a place rather than "Ny".
    if ((tld === 'gov' || tld === 'us') && label && US_STATE_NAMES[label]) {
      return US_STATE_NAMES[label];
    }
    // Otherwise prefer a publisher named in the page title's trailing segment,
    // which reads better than a smushed domain label (e.g. "cityandstateny") —
    // but only when that segment actually names this host. See
    // `titleSegmentNamesHost`: an unchecked segment renders a VoteMate topic
    // label as the publisher.
    const fromTitle = publisherFromTitle(source.title);
    if (fromTitle && titleSegmentNamesHost(fromTitle, host)) return fromTitle;
    if (label) return titleCaseLabel(label);
  }
  return publisherFromTitle(source.title) ?? getSourceTypeLabel(source.type);
}

/**
 * Source type labels the Sources menu groups by, in the order they appear.
 *
 * Fixed rather than derived from the answer, so the menu reads the same way on
 * every turn: VoteMate's own civic-data providers first, then official records,
 * then advocacy and campaign material, with the open web and news last.
 */
export const SOURCE_GROUP_ORDER = [
  'ballotpedia',
  'democracy_works',
  'government',
  'nonprofit',
  'candidate',
  'website',
  'news',
] as const;

export type SourceGroupType = (typeof SOURCE_GROUP_ORDER)[number];

export interface SourceGroupRow {
  source: SourceItem;
  /** Position in the cited-sources array — unique, so safe for DOM ids. */
  index: number;
  /** Position within this group, 1-based. What the reader sees. */
  n: number;
}

export interface SourceGrouping {
  groups: { type: SourceGroupType; rows: SourceGroupRow[] }[];
  /** Cited-sources index → the number shown for it, scoped to its group. */
  displayNumber: Map<number, number>;
  /** Cited-sources index → how many rows its group holds. */
  groupSize: Map<number, number>;
  /** Cited-sources index → how many cited sources share its domain (drives
   *  whether a chip needs an ordinal — a domain cited once needs none). */
  sameDomainCount: Map<number, number>;
}

/** The group a source belongs to, falling back to the open web. */
export function sourceGroupType(source: SourceItem): SourceGroupType {
  const type = source.type ?? determineSourceType(source.url ?? '', source.title ?? '');
  return (SOURCE_GROUP_ORDER as readonly string[]).includes(type as string)
    ? (type as SourceGroupType)
    : 'website';
}

/**
 * Bucket cited sources by type label and number them **within each group**.
 *
 * The displayed number is a source's position in its own group, not its
 * position in the whole list. That keeps ordinals small — a group holds a
 * handful of rows where the full list can hold twenty — which is what makes
 * them read as superscripts on the inline chips rather than as footnote
 * numbers. The cost is that a number repeats across groups: there is a `1`
 * under Ballotpedia and a `1` under Web. Chips carry the publisher's name and
 * favicon alongside the number, so the pair still identifies one row.
 *
 * Rows keep their array `index` separately, because DOM ids have to stay
 * unique and the display number no longer is.
 *
 * Shared by the Sources menu and the inline citation chips so the two can
 * never drift into different numbering.
 */
export function groupSources(sources: SourceItem[]): SourceGrouping {
  const entries = sources.map((source, index) => ({ source, index }));
  const groups: SourceGrouping['groups'] = [];
  const displayNumber = new Map<number, number>();
  const groupSize = new Map<number, number>();

  // Give each source its domain's cited-count, so a chip can check "same domain
  // cited more than once?" without rescanning.
  const domainTotals = new Map<string, number>();
  for (const { source } of entries) {
    const host = hostnameOf(source.url);
    if (host) domainTotals.set(host, (domainTotals.get(host) ?? 0) + 1);
  }
  const sameDomainCount = new Map<number, number>();
  for (const { source, index } of entries) {
    const host = hostnameOf(source.url);
    sameDomainCount.set(index, host ? (domainTotals.get(host) ?? 1) : 1);
  }

  for (const type of SOURCE_GROUP_ORDER) {
    const inGroup = entries.filter((entry) => sourceGroupType(entry.source) === type);
    if (inGroup.length === 0) continue;
    const rows = inGroup.map((entry, i) => ({ ...entry, n: i + 1 }));
    for (const row of rows) {
      displayNumber.set(row.index, row.n);
      groupSize.set(row.index, rows.length);
    }
    groups.push({ type, rows });
  }

  return { groups, displayNumber, groupSize, sameDomainCount };
}

/**
 * Publisher/site favicon URL via Google's public favicon service — the
 * standard, zero-maintenance way to show a site's own logo. Falls back to a
 * generic glyph in the UI when the image fails to load.
 */
export function getFaviconUrl(url?: string, size = 64): string | undefined {
  const host = hostnameOf(url);
  if (!host) return undefined;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=${size}`;
}

/** Human label for the chip. */
export function getSourceTypeLabel(type?: SourceItem['type']): string {
  switch (type) {
    case 'democracy_works':
      return i18n.t('sources.typeLabels.democracyWorks', { ns: 'chat' });
    case 'government':
      return i18n.t('sources.typeLabels.government', { ns: 'chat' });
    case 'news':
      return i18n.t('sources.typeLabels.news', { ns: 'chat' });
    case 'candidate':
      return i18n.t('sources.typeLabels.candidate', { ns: 'chat' });
    case 'nonprofit':
      return i18n.t('sources.typeLabels.nonprofit', { ns: 'chat' });
    case 'ballotpedia':
      return i18n.t('sources.typeLabels.ballotpedia', { ns: 'chat' });
    case 'website':
    default:
      return i18n.t('sources.typeLabels.web', { ns: 'chat' });
  }
}

/**
 * Put every citation on a claim its source actually supports, in code.
 *
 * ## Why this is not a prompt rule
 *
 * A `[N]` marker is an integer the model copies out of a listing, and an integer
 * carries no evidence of what it is for. A borrowed number resolves to a *real,
 * stamped* source, so nothing downstream could tell it apart from a correct
 * citation — which is how a five-candidate answer shipped with one candidate's
 * campaign site on every other candidate's positions. Prompt rules change how
 * often that happens; they cannot make it checkable.
 *
 * Two facts already in the data make it checkable, and then fixable:
 *
 *  - **`subject`** — who or what a source is about. The profile lookup knows
 *    whose profile it flattened; the endorsement lookups know whose page they
 *    read.
 *  - **`topic`** — which position of that subject the source evidences.
 *
 * Together they are the whole of what a profile asserts: *this candidate's stance
 * on this topic came from this page.* The answer says the same thing in its own
 * structure — a section per candidate, a bullet per topic — so the two can be
 * matched, and a marker that lands in the wrong section can be moved to the right
 * source rather than merely refused.
 *
 * ## What this pass does
 *
 * Rewrites the body once, before anything resolves a marker, so every downstream
 * step — resolution, numbering, clustering, the Sources panel — sees a body whose
 * citations already agree with their sources.
 *
 * **Every marker the model wrote** is kept, moved, or removed:
 *
 *  - **Keep** it when its source claims no subject, when nothing in the text says
 *    otherwise, when the section it sits in is that subject's, or when its own
 *    line names that subject — the last of which is what keeps "unlike Pierce,
 *    Pritzker declines public financing" citing Pierce.
 *  - **Move** it to the section subject's own source for the claim, chosen by the
 *    tiers on `sourceFor` below: the same topic, one topic name inside the other,
 *    shared significant words, the publisher named in prose, or the subject's own
 *    page.
 *  - **Remove** it when no source of that subject answers to the claim at all.
 *
 * **Every claim the model left bare** is then given the source that answers to
 * it, by those same tiers. This is the half that decides how much of a turn's
 * evidence a reader can actually see: the model writes two or three markers for
 * a section of ten bullets, so moving the ones it wrote leaves most of the answer
 * reading as unsourced assertion. And the Sources menu is built from the
 * citations that survive here, so a page this pass fails to place is one the
 * voter never learns the answer stood on. A profile's evidence URLs and a
 * candidate's Ballotpedia page reach the body because this pass puts them there,
 * not because the model remembered a number.
 *
 * Deliberately conservative at every step: a source with no subject is never
 * touched, a body whose sources name no subject at all is returned unchanged, a
 * heading naming two subjects opens no section, an ambiguous choice among
 * topic-specific pages is declined, and a claim line outside a section is only
 * ever given its subject's own whole-subject page. It fails open — a turn this
 * cannot reason about is left exactly as the model wrote it.
 */

/** A line that declares whose section follows: a heading, or a bold lead-in. */
const DECLARING_LINE = /^\s{0,3}(?:#{1,6}\s+(.*)|\*\*([^*]+)\*\*)/;

/** A third-person reference standing in for a name. Matched on an
 *  already-normalized string, so these are whole words. */
const THIRD_PERSON = /(^| )(he|him|his|she|her|hers|they|them|their|theirs)( |$)/;

/** The bold label opening a bullet — "- **Cost of living**: …" — or a bold lead
 *  on a plain line. What the answer calls the topic this claim is about. */
const LEAD_IN = /^\s*(?:(?:[-*+]|\d+[.)])\s+)?\*\*([^*]+)\*\*/;

/** A run of adjacent markers with the single space it sits in, so a run that
 *  empties out takes that space with it: "…Act [9]." reads "…Act." */
const CITATION_RUN = /([ \t]?)((?:\[\d+\])+)/g;

const MARKER = /\[(\d+)\]/g;

/** Whether a line carries any citation at all, which is what separates a claim
 *  the model sourced from one it left bare. */
const HAS_MARKER = /\[\d+\]/;

/** A list item's opener, and a markdown heading — the two shapes that say
 *  whether a line makes a claim or introduces the ones below it. */
const LIST_ITEM = /^\s{0,6}(?:[-*+]|\d+[.)])\s+/;
const HEADING = /^\s{0,3}#{1,6}\s/;

/** Trailing whitespace, so an appended citation lands *inside* a markdown hard
 *  break rather than after it — two trailing spaces still end the line. */
const TRAILING_SPACE = /\s*$/;

/** How much prose a line needs before it counts as a claim worth citing. Long
 *  enough to exclude a bold sub-heading standing on its own ("**Backing**"),
 *  short enough to include the shortest real sentence an answer writes. */
const MIN_CLAIM_CHARS = 24;

/** Letters, digits and single spaces, lowercased — so "J.B. Pritzker (D)" and
 *  "J.B. Pritzker" compare equal on the part they share. */
function normalizeName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Tokens of a name worth matching on: drops initials and common suffixes, which
 *  are the parts a heading is most likely to write differently. */
function nameTokens(value: string): string[] {
  const skip = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'dr', 'mr', 'ms', 'mrs']);
  return normalizeName(value)
    .split(' ')
    .filter((token) => token.length > 1 && !skip.has(token));
}

/** Words shared by half the topic labels in politics, so two labels are compared
 *  on the part that actually distinguishes them. Without this, "Housing policy"
 *  and "Immigration policy" share a word and score as related. */
const TOPIC_NOISE = new Set([
  'and',
  'the',
  'for',
  'from',
  'with',
  'our',
  'their',
  'his',
  'her',
  'its',
  'your',
  'more',
  'other',
  'own',
  'words',
  'issue',
  'issues',
  'policy',
  'policies',
  'plan',
  'plans',
  'position',
  'positions',
  'stance',
  'stances',
  'agenda',
  'record',
  'views',
  'general',
  // A campaign-site row is titled "<Name> — Campaign website", so these three
  // words sit in the boilerplate of a title rather than in what the page is
  // about. Left scoring, "**Campaign finance**" shares a word with every
  // candidate's campaign site and picks it over the page that actually
  // evidenced the claim.
  'campaign',
  'website',
  'site',
]);

/** Significant tokens of a topic label, for scoring one label against another. */
function topicTokens(value: string): string[] {
  return normalizeName(value)
    .split(' ')
    .filter((token) => token.length > 2 && !TOPIC_NOISE.has(token));
}

interface SubjectMatcher {
  subject: string;
  /** Normalized full name, e.g. "j b pritzker". */
  full: string;
  /** Last name, used only when it identifies exactly one subject in the turn. */
  distinctive: string;
}

/** Whether `haystack` (already normalized) names this subject. */
function mentions(haystack: string, matcher: SubjectMatcher): boolean {
  if (matcher.full && haystack.includes(matcher.full)) return true;
  if (!matcher.distinctive) return false;
  // Word-boundary check on a space-normalized string, so "bailey" does not match
  // inside "baileys".
  return ` ${haystack} `.includes(` ${matcher.distinctive} `);
}

function buildMatchers(subjects: string[]): SubjectMatcher[] {
  const counts = new Map<string, number>();
  const parsed = subjects.map((subject) => {
    const tokens = nameTokens(subject);
    const last = tokens.length ? tokens[tokens.length - 1] : '';
    counts.set(last, (counts.get(last) ?? 0) + 1);
    return { subject, full: normalizeName(subject), last };
  });
  return parsed.map(({ subject, full, last }) => ({
    subject,
    full,
    // A surname shared by two subjects in one turn identifies neither.
    distinctive: last && counts.get(last) === 1 ? last : '',
  }));
}

/** The registrable label of a URL's host — "ballotpedia" for ballotpedia.org.
 *  What a sentence naming its source in prose is likely to have written. */
function hostLabel(url?: string): string {
  if (!url) return '';
  try {
    const parts = new URL(url).hostname
      .toLowerCase()
      .replace(/^www\./, '')
      .split('.');
    return parts.length >= 2 ? parts[parts.length - 2] : parts[0];
  } catch {
    return '';
  }
}

/** Every way this source names what it is about: its `topic`, plus each segment
 *  of its title, so a bullet reading "Housing" matches a source titled
 *  "… — Housing" even when the profile's topic label is "Affordability". */
function topicNames(source: { topic?: string; title?: string }): string[] {
  const out: string[] = [];
  if (typeof source.topic === 'string' && source.topic.trim()) {
    out.push(normalizeName(source.topic));
  }
  for (const segment of (source.title || '').split(/[—–|:-]/)) {
    const name = normalizeName(segment);
    if (name) out.push(name);
  }
  return out;
}

/** Whether the tool that produced this source said it covers its subject as a
 *  whole — a Ballotpedia profile, a campaign site — rather than the one position
 *  it was cited for. Default-deny, matching the backend: an unmarked source
 *  stands for one claim only. */
function coversWholeSubject(source: { scope?: string }): boolean {
  return source.scope === 'subject';
}

/** The one source in `matches`, or nothing when the list does not name one.
 *
 *  A tie among a subject's whole-subject pages is broken by array order rather
 *  than declined: they are interchangeable by construction — each covers
 *  everything the answer says about that subject — so refusing to choose only
 *  costs the claim its citation. A tie among topic-specific pages is declined,
 *  because there the choice is a claim about which page evidences what. */
function only<S extends { scope?: string }>(matches: readonly S[]): S | undefined {
  if (matches.length === 1) return matches[0];
  if (matches.length === 0) return undefined;
  const general = matches.filter(coversWholeSubject);
  return general.length ? general[0] : undefined;
}

/** Line offsets and section boundaries for one body, with the lookups that read
 *  them. Built per pass, because a pass that rewrites the text moves every
 *  offset after the first edit.
 *
 *  A declaring line naming exactly one subject opens that subject's section; one
 *  naming two (a comparison heading) leaves the current section alone rather
 *  than guessing. */
function indexBody(text: string, matchers: readonly SubjectMatcher[]) {
  const sections: Array<{ at: number; subject: string }> = [];
  const lines: Array<{ at: number; end: number; text: string }> = [];
  // The turn's subject when it has exactly one. An answer about a single
  // candidate writes their name once and then says "he" — "Where he stands",
  // "His record" — so requiring the name in every heading leaves the answer
  // that needs sections most, a full profile, with none at all. Safe precisely
  // because there is one subject: no other person's source exists in this turn
  // to put behind the claim.
  const sole = matchers.length === 1 ? matchers[0] : undefined;
  let at = 0;
  for (const line of text.split('\n')) {
    lines.push({ at, end: at + line.length, text: line });
    const declaring = DECLARING_LINE.exec(line);
    if (declaring) {
      const heading = normalizeName(declaring[1] ?? declaring[2] ?? '');
      const named = matchers.filter((matcher) => mentions(heading, matcher));
      if (named.length === 1) sections.push({ at, subject: named[0].subject });
      else if (named.length === 0 && sole && THIRD_PERSON.test(heading)) {
        sections.push({ at, subject: sole.subject });
      }
    }
    at += line.length + 1;
  }

  const lineAt = (offset: number) => {
    for (const line of lines) {
      if (offset <= line.end) return line;
    }
    return lines[lines.length - 1];
  };

  const sectionAt = (offset: number): string => {
    let current = '';
    for (const section of sections) {
      if (section.at > offset) break;
      current = section.subject;
    }
    return current;
  };

  /** Whose claim a marker sits on: the open section, or — before the first one —
   *  the sole subject its own line names. An intro sentence about one
   *  candidate's record is as much a claim about that candidate as a bullet
   *  under their heading is. */
  const contextAt = (offset: number, normalizedLine: string): string => {
    const section = sectionAt(offset);
    if (section) return section;
    const named = matchers.filter((matcher) => mentions(normalizedLine, matcher));
    return named.length === 1 ? named[0].subject : '';
  };

  return { lines, lineAt, sectionAt, contextAt };
}

/**
 * What to print under a source row's title, and in which form.
 *
 * The backend says who wrote a row's `description`; this turns that into a
 * rendering decision, once, so web, mobile and the widget cannot disagree about
 * which kinds print or how. Four provenances collapse to three forms:
 *
 *  - **`quote`** → a quotation, with the date the record stamped on it. The
 *    candidate's own words as the record publishes them, which is the strongest
 *    thing this slot can hold: a page summary says what the link is, a quote is
 *    the sentence the claim rests on.
 *  - **`publisher`** and **`extract`** → prose. The page's own summary, or
 *    VoteMate's extraction of what the page records the subject saying. Both
 *    answer "what is at this link" in a sentence, and an extract names its own
 *    speaker ("Ross says…"), so neither can be read as the other's author.
 *  - **`dataset`** → a metadata line. A field from the authoritative record —
 *    Congress.gov's latest action, Ballotpedia's endorser type. Not a sentence
 *    anyone wrote about the page, so it is not set as one.
 *
 * Anything else prints nothing. Default-deny matches the backend: a marker this
 * build does not recognise is a marker it cannot render honestly.
 */
export type SubtextForm = 'quote' | 'prose' | 'meta' | 'none';

export interface SourceSubtext {
  form: SubtextForm;
  /** The text to print, empty when `form` is `'none'`. */
  text: string;
  /** Set only for a `quote` the record dated. */
  date?: string;
}

export function sourceSubtext(source: {
  description?: string;
  description_source?: string;
  description_date?: string;
}): SourceSubtext {
  const text = (source.description || '').trim();
  if (!text) return { form: 'none', text: '' };
  switch (source.description_source) {
    case 'quote': {
      const date = (source.description_date || '').trim();
      return date ? { form: 'quote', text, date } : { form: 'quote', text };
    }
    case 'publisher':
    case 'extract':
      return { form: 'prose', text };
    case 'dataset':
      return { form: 'meta', text };
    default:
      return { form: 'none', text: '' };
  }
}

export interface CitationRepair {
  /** The body with each marker kept, renumbered, or removed, and each bare claim
   *  given the source that answers to it. The only field a renderer reads. */
  text: string;
  /** How many markers were moved onto the right source. */
  repaired: number;
  /** How many were removed for want of one. */
  dropped: number;
  /** How many claims the model left bare were given a citation. */
  attached: number;
}

// The three counts are diagnostics, deliberately not consumed: every caller
// destructures `text` alone. They are how a test says *what* the pass did rather
// than only what it produced — a case asserting `attached === 6` fails loudly
// when a widened tier stops firing, where comparing whole bodies would not say
// which half changed. They are also the shape a turn-metrics hook would read if
// this is ever measured against real answers, which is the open question on the
// PR: how often the last tier is the one doing the work.

export function repairCitations<
  S extends {
    subject?: string;
    topic?: string;
    title?: string;
    url?: string;
    scope?: string;
    cite_index?: number;
  },
>(text: string, sources: readonly S[]): CitationRepair {
  const unchanged = { text, repaired: 0, dropped: 0, attached: 0 };
  if (!text || sources.length === 0) return unchanged;

  const subjects = Array.from(
    new Set(
      sources
        .map((source) => (typeof source?.subject === 'string' ? source.subject.trim() : ''))
        .filter(Boolean)
    )
  );
  // Nothing to work with: no source names who it is about, so no claim can be
  // matched to one. A turn with a single subject still runs — pass 1 is a no-op
  // there (every marker fits the only subject there is), but pass 2 is the half
  // that puts a source on a bare claim, and a one-candidate answer is exactly
  // where the model writes a marker on two bullets out of eight.
  if (subjects.length === 0) return unchanged;

  // `[N]` → the source it names. Mirrors the resolvers: `cite_index` when the
  // array is stamped, position only when nothing in it is.
  const stamped = sources.some((source) => typeof source?.cite_index === 'number');
  const byNumber = new Map<number, S>();
  const numberOf = new Map<S, number>();
  sources.forEach((source, i) => {
    if (typeof source?.cite_index !== 'number' && stamped) return;
    const n = typeof source?.cite_index === 'number' ? source.cite_index : i + 1;
    numberOf.set(source, n);
    if (!byNumber.has(n)) byNumber.set(n, source);
  });

  const matchers = buildMatchers(subjects);
  const bySubject = new Map<string, S[]>();
  for (const source of sources) {
    const subject = typeof source?.subject === 'string' ? source.subject.trim() : '';
    if (!subject) continue;
    const list = bySubject.get(subject);
    if (list) list.push(source);
    else bySubject.set(subject, [source]);
  }

  /** This subject's own source for this claim, or nothing when none answers to
   *  it. Tried most specific first, so a page that evidenced exactly this topic
   *  beats one that merely shares a word with it:
   *
   *   1. **The same topic**, spelled the same way. What the profile grounded.
   *   2. **One topic name inside the other** — a bullet reading "Healthcare"
   *      against a source titled "… — Healthcare Access".
   *   3. **Shared significant words.** The profile writes a priority label and
   *      the answer writes its own heading for the same subject matter, so
   *      "Government/corporate power" and "End Corporate Capture of Government"
   *      never match as strings and are plainly the same subject.
   *   4. **The publisher, named in prose** — "recorded on Ballotpedia".
   *
   *  A claim none of these answer to stays bare. The subject's whole-subject
   *  page (a Ballotpedia profile) used to be the last resort here, and it put
   *  one chip under every fact in the section — the page says who the answer
   *  is about, not which page evidenced a given claim. That page is still
   *  offered, by `wholeSubjectFor`, to a line outside any section that names
   *  the subject alone, where it stands for the line's identity, not its proof.
   */
  const sourceFor = (context: string, line: string): S | undefined => {
    const candidates = bySubject.get(context);
    if (!candidates || candidates.length === 0) return undefined;

    const lead = LEAD_IN.exec(line);
    const leadName = lead ? normalizeName(lead[1]) : '';
    if (leadName) {
      const exact = only(
        candidates.filter((source) => topicNames(source).some((name) => name === leadName))
      );
      if (exact) return exact;

      // Four characters keeps a short fragment from being "inside" everything.
      const contained = only(
        candidates.filter((source) =>
          topicNames(source).some(
            (name) =>
              name.length >= 4 &&
              leadName.length >= 4 &&
              (name.includes(leadName) || leadName.includes(name))
          )
        )
      );
      if (contained) return contained;

      // Best overlap wins outright: two shared words beat one rather than tying
      // with it, so the closest label is picked instead of the choice being
      // declined for having more than one candidate.
      const leadTokens = topicTokens(lead ? lead[1] : '');
      if (leadTokens.length) {
        let best = 0;
        let bestSources: S[] = [];
        for (const source of candidates) {
          const tokens = new Set(topicNames(source).flatMap((name) => topicTokens(name)));
          const overlap = leadTokens.filter((token) => tokens.has(token)).length;
          if (overlap === 0 || overlap < best) continue;
          if (overlap > best) {
            best = overlap;
            bestSources = [source];
          } else {
            bestSources.push(source);
          }
        }
        const scored = only(bestSources);
        if (scored) return scored;
      }
    }

    const normalizedLine = normalizeName(line);
    const named = only(
      candidates.filter((source) => {
        const label = hostLabel(source.url);
        return label.length > 3 && ` ${normalizedLine} `.includes(` ${label} `);
      })
    );
    return named;
  };

  /** The one subject this line names, or nothing when it names none or several. */
  const subjectNamedAlone = (line: string): string => {
    const normalized = normalizeName(line);
    const named = matchers.filter((matcher) => mentions(normalized, matcher));
    return named.length === 1 ? named[0].subject : '';
  };

  /** This subject's own page — the one source that backs whatever the answer
   *  says about them. */
  const wholeSubjectFor = (subject: string): S | undefined =>
    (bySubject.get(subject) ?? []).filter(coversWholeSubject)[0];

  let repaired = 0;
  let dropped = 0;

  // Pass 1 — every marker the model wrote ends up on a source that fits the
  // claim it sits on.
  const index = indexBody(text, matchers);
  const rebuilt = text.replace(
    CITATION_RUN,
    (_whole: string, gap: string, run: string, offset: number) => {
      const runAt = offset + gap.length;
      const line = index.lineAt(runAt);
      const normalizedLine = normalizeName(line.text);
      const context = index.contextAt(runAt, normalizedLine);

      const kept: number[] = [];
      for (const marker of run.match(MARKER) ?? []) {
        const num = parseInt(marker.slice(1, -1), 10);
        const source = byNumber.get(num);

        // Unresolvable already: leave it exactly as it was, so the resolvers
        // keep making that call in one place.
        if (!source) {
          if (!kept.includes(num)) kept.push(num);
          continue;
        }

        const subject = typeof source.subject === 'string' ? source.subject.trim() : '';
        const fits =
          !subject ||
          !context ||
          context === subject ||
          matchers.some(
            (matcher) => matcher.subject === subject && mentions(normalizedLine, matcher)
          );
        if (fits) {
          if (!kept.includes(num)) kept.push(num);
          continue;
        }

        const replacement = sourceFor(context, line.text);
        const replacementNum = replacement ? numberOf.get(replacement) : undefined;
        if (replacementNum !== undefined) {
          repaired += 1;
          if (!kept.includes(replacementNum)) kept.push(replacementNum);
        } else {
          dropped += 1;
        }
      }

      if (kept.length === 0) return '';
      return `${gap}${kept.map((n) => `[${n}]`).join('')}`;
    }
  );

  // Pass 2 — a claim the model left bare gets the source that answers to it.
  //
  // Pass 1 can only move a marker the model already wrote, and the model writes
  // far fewer than the answer has claims: a section of ten bullets arrives with
  // two or three, and the rest read as unsourced assertions. But every source in
  // the Sources menu is one this answer was built from, so a bullet in a
  // candidate's section carrying no marker is a claim whose source is sitting
  // one panel away, unlinked. Placing it is the same decision pass 1 already
  // makes, offered for the lines it was never offered for — which is what puts a
  // profile's evidence URLs and a candidate's Ballotpedia page into the body
  // instead of leaving them to whether the model remembered to write a number.
  //
  // Deliberately narrow: a claim line, inside a subject's section, with nothing
  // cited on it yet, and only when a source answers to it. `sectionAt` rather
  // than `contextAt` — a heading is an explicit statement of whose section this
  // is, while a passing mention of one name in a sentence is not, and a citation
  // this pass adds is not one the model asked for.
  const rebuiltIndex = indexBody(rebuilt, matchers);
  const filled: string[] = [];
  let attached = 0;
  for (const line of rebuiltIndex.lines) {
    filled.push(line.text);
    if (HAS_MARKER.test(line.text) || HEADING.test(line.text)) continue;
    if (!LIST_ITEM.test(line.text) && !LEAD_IN.test(line.text)) continue;
    if (line.text.replace(LEAD_IN, '').trim().length < MIN_CLAIM_CHARS) continue;
    const context = rebuiltIndex.sectionAt(line.at);
    // Outside a section, the line's own name is what says whose claim it is —
    // "- **Ted Lieu** (D) — U.S. House, CA-36" is about Lieu whether or not a
    // heading said so, and an answer that lists people one per bullet opens no
    // sections at all. Only that subject's whole-subject page is offered here:
    // a heading is an explicit statement of scope and a passing name is not, so
    // the tiers that pick a topic-specific page stay behind one.
    const named = context ? '' : subjectNamedAlone(line.text);
    if (!context && !named) continue;
    const source = context ? sourceFor(context, line.text) : wholeSubjectFor(named);
    const num = source ? numberOf.get(source) : undefined;
    if (num === undefined) continue;
    filled[filled.length - 1] = line.text.replace(TRAILING_SPACE, (tail) => ` [${num}]${tail}`);
    attached += 1;
  }

  return { text: filled.join('\n'), repaired, dropped, attached };
}
